import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { addMinutes, format } from 'date-fns'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { CUSTOMS_OFFICES_OF_TRANSIT_MULTIPLICITY } from 'shared/ncts/common/constants'
import { excludeDeleted } from 'shared/lib/common-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankCustomsOfficeOfTransitDeclared } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'

import { LOCAL_DATE_TIME_WITHOUT_TZ_FORMAT } from 'shared/lib/datetime'
import RadioGroup from 'components/p5/RadioGroup'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'transitCustomsOffices'

function CustomsOfficeOfTransit() {
  const { control } = useFormContext<TransitOperationFormValues>()

  const { t } = useTranslation()
  const minArrivalDateAndTime = addMinutes(new Date(), 30)

  const defaultCustomsOfficeOfTransitDeclared = blankCustomsOfficeOfTransitDeclared
  defaultCustomsOfficeOfTransitDeclared.arrivalDateAndTime = format(minArrivalDateAndTime, LOCAL_DATE_TIME_WITHOUT_TZ_FORMAT)

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
    fields,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, 'transitCustomsOffices'>({
    control,
    name: scope,
  })

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.arrivalDateAndTime`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.entryIndicator`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.reEntryIndicator`),
  ], [visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)

  const [, transitCustomsOfficesOptions] = useCodelist('TRANSIT_CUSTOMS_OFFICES')

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'
  const isAddButtonDisabled = isAddForbidden || fields.filter(excludeDeleted).length >= CUSTOMS_OFFICES_OF_TRANSIT_MULTIPLICITY

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-offices-of-transit"
        headingLabel={t('declaration.p5.customsOfficeOfTransit')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const transitCustomsOffices = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}`

          return (
            <FormFieldArray
              key={`transitCustomsOffice_${transitCustomsOffices.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={transitCustomsOffices.sequenceNumber + 1}
              hidden={transitCustomsOffices.deleted}
            >
              <FormField labelKey="declaration.p5.referenceNumber">
                <FormSelect
                  field={`${innerItemScope}.referenceNumber`}
                  labelKey="declaration.p5.referenceNumber"
                  options={transitCustomsOfficesOptions}
                  type="sync"
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.arrivalDateAndTime"
                fieldRule={fieldRequirements[`${innerItemScope}.arrivalDateAndTime`]}
              >
                <Input
                  type="datetime-local"
                  step={1}
                  field={`${innerItemScope}.arrivalDateAndTime`}
                  labelKey="declaration.p5.arrivalDateAndTime"
                  min={format(minArrivalDateAndTime, LOCAL_DATE_TIME_WITHOUT_TZ_FORMAT)}
                  fieldRule={fieldRequirements[`${innerItemScope}.arrivalDateAndTime`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.entryIndicator"
                fieldRule={fieldRequirements[`${innerItemScope}.entryIndicator`]}
              >
                <RadioGroup
                  field={`${innerItemScope}.entryIndicator`}
                  labelKey="common.no_entryIndicator"
                  type="radio"
                  fieldRule={fieldRequirements[`${innerItemScope}.entryIndicator`]}
                  radioOptions={[
                    { label: 'common.no_entryIndicator', value: 'false' },
                    { label: 'common.yes_entryIndicator', value: 'true' },
                  ]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.reEntryIndicator"
                fieldRule={fieldRequirements[`${innerItemScope}.reEntryIndicator`]}
              >
                <RadioGroup
                  field={`${innerItemScope}.reEntryIndicator`}
                  type="radio"
                  fieldRule={fieldRequirements[`${innerItemScope}.reEntryIndicator`]}
                  radioOptions={[
                    { label: 'common.no_entryIndicator', value: 'false' },
                    { label: 'common.yes_entryIndicator', value: 'true' },
                  ]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(defaultCustomsOfficeOfTransitDeclared)}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewCustomsOfficeOfTransit')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default CustomsOfficeOfTransit
